import React, { useEffect, useState } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import toast from "react-hot-toast";
import Loader from "../../assets/Loader.gif";

const BASE_URL = "https://admin.chatindiacom.cloud/api";

function LevelList() {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);

  const [form, setForm] = useState({
    level_number: "",
    title: "",
    coins_required: "",
    entry_effect: null,
    entry_effect_duration_ms: "",
    remove_entry_effect: false,
  });

  const getToken = () => localStorage.getItem("authToken");

  const api = axios.create({
    baseURL: BASE_URL,
    headers: { Authorization: `Bearer ${getToken()}` },
  });

  const fetchLevels = async () => {
    setLoading(true);
    try {
      const res = await api.get("/user-levels/list");
      setLevels(res.data.levels || []);
    } catch (err) {
      toast.error("Failed to load levels");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setEditingLevel(null);
    setForm({
      level_number: "",
      title: "",
      coins_required: "",
      entry_effect: null,
      entry_effect_duration_ms: "",
      remove_entry_effect: false,
    });
    setOpen(true);
  };

  const openEditModal = (lvl) => {
    setEditingLevel(lvl);
    setForm({
      level_number: lvl.level_number,
      title: lvl.title,
      coins_required: lvl.coins_required,
      entry_effect: null,
      entry_effect_duration_ms: lvl.entry_effect_duration_ms || "",
      remove_entry_effect: false,
    });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (!form.level_number || !form.title || !form.coins_required) {
      toast.error("Please fill all fields");
      return;
    }

    const fd = new FormData();
    fd.append("level_number", form.level_number);
    fd.append("title", form.title);
    fd.append("coins_required", form.coins_required);
    if (form.entry_effect) fd.append("entry_effect", form.entry_effect);
    if (form.entry_effect_duration_ms) {
      fd.append("entry_effect_duration_ms", form.entry_effect_duration_ms);
    }
    if (editingLevel && form.remove_entry_effect) {
      fd.append("remove_entry_effect", "1");
    }

    setSaving(true);
    try {
      if (editingLevel) {
        fd.append("id", editingLevel.id);
        await api.post("/user-levels/edit", fd);
        toast.success("Level updated successfully");
      } else {
        await api.post("/user-levels/create", fd);
        toast.success("Level added successfully");
      }
      handleClose();
      fetchLevels();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this level?")) return;
    try {
      await api.post("/user-levels/delete", { id });
      toast.success("Level deleted");
      fetchLevels();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const renderEffectPreview = (lvl) => {
    if (!lvl.entry_effect) return <span className="text-xs">—</span>;
    if (lvl.entry_effect_type === "lottie") {
      return (
        <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-md">
          Lottie JSON
        </span>
      );
    }
    return (
      <img
        src={lvl.entry_effect}
        alt="effect"
        className="object-cover w-9 h-9 rounded-md"
      />
    );
  };

  return (
    <div className="min-h-screen px-4 py-6 dark:bg-primary md:px-8 xl:ml-72">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold font-poppins text-[#000000] dark:text-darkText">
          User Levels
        </h2>
        <button
          onClick={openAddModal}
          style={{ backgroundColor: "#6c5ce7" }}
          className="px-4 py-2 text-sm font-semibold text-white rounded-lg font-poppins shadow-sm hover:opacity-90"
        >
          + Add Level
        </button>
      </div>

      <div className="overflow-x-auto bg-white border border-[#F2F2F2] rounded-xl dark:bg-primary dark:border-[#1F1F1F]">
        <table className="w-full text-sm text-left font-poppins">
          <thead>
            <tr className="text-[#000000] dark:text-darkText border-b border-[#F2F2F2] dark:border-[#1F1F1F]">
              <th className="px-4 py-3">Level No.</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Coins Required</th>
              <th className="px-4 py-3">Entry Effect</th>
              <th className="px-4 py-3">Duration (ms)</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-6 text-center text-[#5B5B5B] dark:text-[#AAAAAA]">
                  Loading...
                </td>
              </tr>
            ) : levels.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-6 text-center text-[#5B5B5B] dark:text-[#AAAAAA]">
                  No levels found. Click "Add Level" to create one.
                </td>
              </tr>
            ) : (
              levels.map((lvl) => (
                <tr
                  key={lvl.id}
                  className="border-b border-[#F2F2F2] dark:border-[#1F1F1F] text-[#5B5B5B] dark:text-[#AAAAAA]"
                >
                  <td className="px-4 py-3">{lvl.level_number}</td>
                  <td className="px-4 py-3">{lvl.title}</td>
                  <td className="px-4 py-3">{lvl.coins_required}</td>
                  <td className="px-4 py-3">{renderEffectPreview(lvl)}</td>
                  <td className="px-4 py-3">{lvl.entry_effect_duration_ms || "—"}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openEditModal(lvl)}
                      className="px-3 py-1 mr-2 text-xs bg-[#F2F2F2] dark:bg-[#1F1F1F] dark:text-darkText rounded-md"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(lvl.id)}
                      className="px-3 py-1 text-xs text-white bg-red-500 rounded-md"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Modal */}
      <Dialog open={open} onClose={handleClose} fullWidth className="custom-dialog">
        <DialogContent className="flex flex-col gap-4 dark:bg-primary">
          <h2 className="text-xl text-center font-poppins text-[#000000] dark:text-darkText">
            {editingLevel ? "Edit Level" : "Add Level"}
          </h2>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              Level Number
            </label>
            <input
              type="number"
              value={form.level_number}
              onChange={(e) => setForm({ ...form, level_number: e.target.value })}
              className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
              placeholder="e.g. 1"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              Title
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
              placeholder="e.g. Bronze"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              Coins Required
            </label>
            <input
              type="number"
              value={form.coins_required}
              onChange={(e) => setForm({ ...form, coins_required: e.target.value })}
              className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
              placeholder="e.g. 1000"
            />
          </div>

          <hr className="border-[#F2F2F2] dark:border-[#1F1F1F]" />

          <div className="flex flex-col gap-1">
            <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              Entry Effect (.json Lottie / .gif / .webp / .png — max 5MB)
            </label>
            <input
              type="file"
              accept=".json,.gif,.webp,.png"
              onChange={(e) => setForm({ ...form, entry_effect: e.target.files[0] })}
              className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText"
            />
            {editingLevel?.entry_effect && (
              <p className="text-xs text-[#5B5B5B] dark:text-[#AAAAAA]">
                Current: {editingLevel.entry_effect_type === "lottie" ? "Lottie animation set" : "Image/GIF set"}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              Effect Duration in ms (optional, 500–15000)
            </label>
            <input
              type="number"
              min={500}
              max={15000}
              value={form.entry_effect_duration_ms}
              onChange={(e) => setForm({ ...form, entry_effect_duration_ms: e.target.value })}
              className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
              placeholder="e.g. 3000"
            />
          </div>

          {editingLevel?.entry_effect && (
            <label className="flex items-center gap-2 text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              <input
                type="checkbox"
                checked={form.remove_entry_effect}
                onChange={(e) => setForm({ ...form, remove_entry_effect: e.target.checked })}
              />
              Remove existing entry effect
            </label>
          )}
        </DialogContent>

        <DialogContent className="flex justify-center gap-4 pb-4 dark:bg-primary">
          <button
            className="w-[150px] py-2 border border-header text-[#3A3333] dark:text-darkText rounded-lg"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{ backgroundColor: "#0D9947" }}
            className="w-[150px] py-2 text-white rounded-lg"
          >
            {saving ? (
              <div className="flex justify-center py-1">
                <img src={Loader} alt="loader" height={10} width={10} />
              </div>
            ) : (
              "Save"
            )}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LevelList;